import { z } from 'zod'
import type { CarbonRouting } from './carbon'
import type { IGFSummary } from './waste'

// Company ids are no longer a fixed enum: Habilis can introduce customers we
// don't know at build time (they are provisioned on login). We validate shape
// only — the API (`GET /api/companies`) is the source of truth for which
// companies exist and who may see them. The known ÍGF customers below remain as
// the seed / display fallback.
export const CompanyIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9-]+$/)

export type CompanyId = string

export const ALL_COMPANY_IDS: CompanyId[] = [
  'berserkir',
  'berjaya',
  'isavia',
  'landspitali',
  'gardabaer',
  'olgerdin',
  'skagafjordur',
  'gg-verk',
  'orka-natturunnar',
]

export type DataSource = 'live' | 'static'

export interface CompanyMeta {
  id: CompanyId
  name: string
  shortName: string
  kennitala: string | null
  accentColor: string
  dataSource: DataSource
  source?: string
  description?: string
  /**
   * Per-company override of how each waste category splits across disposal
   * routes, used to make the carbon footprint reflect the customer's real waste
   * handling. Absent/null → use the built-in defaults.
   */
  carbonRouting?: CarbonRouting | null
}

export const COMPANY_META: Record<CompanyId, CompanyMeta> = {
  berserkir: {
    id: 'berserkir',
    name: 'Berserkir ehf.',
    shortName: 'Berserkir',
    kennitala: '5501982159',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  berjaya: {
    id: 'berjaya',
    name: 'Berjaya Hotel Reykjavík',
    shortName: 'Berjaya',
    kennitala: '6212976949',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  isavia: {
    id: 'isavia',
    name: 'Isavia ohf.',
    shortName: 'Isavia',
    kennitala: '5502100370',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  landspitali: {
    id: 'landspitali',
    name: 'Landspítali háskólasjúkrahús',
    shortName: 'Landspítali',
    kennitala: '5003002130',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  gardabaer: {
    id: 'gardabaer',
    name: 'Garðabær',
    shortName: 'Garðabær',
    kennitala: '5701696109',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  olgerdin: {
    id: 'olgerdin',
    name: 'Ölgerðin Egill Skallagrímsson',
    shortName: 'Ölgerðin',
    kennitala: '4203697789',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  skagafjordur: {
    id: 'skagafjordur',
    name: 'Skagafjörður',
    shortName: 'Skagafjörður',
    kennitala: '5506982349',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  'gg-verk': {
    id: 'gg-verk',
    name: 'GG verk ehf.',
    shortName: 'GG verk',
    kennitala: '6606061570',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
  'orka-natturunnar': {
    id: 'orka-natturunnar',
    name: 'Orka náttúrunnar',
    shortName: 'ON',
    kennitala: '4711190830',
    accentColor: '#00A04B',
    dataSource: 'live',
  },
}

export interface CompanyGroupRef {
  name: string
  memberCount: number
}

export interface CompanyGroupMember {
  kennitala: string
  name: string
  shortName: string
  companyId: string | null
  summary: IGFSummary | null
  failed: boolean
}

export interface CompanyGroupInfo {
  name: string
  members: CompanyGroupMember[]
}
